import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Mail, Phone, Building2, MessageSquare, Trash2, CheckCircle2, Clock, Archive, Ban } from "lucide-react";

const STATUS_OPTIONS = [
  { key: "new", label: "New", color: "blue", icon: Clock },
  { key: "contacted", label: "Contacted", color: "amber", icon: MessageSquare },
  { key: "qualified", label: "Qualified", color: "emerald", icon: CheckCircle2 },
  { key: "closed", label: "Closed", color: "slate", icon: Archive },
  { key: "spam", label: "Spam", color: "red", icon: Ban },
];

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const qs = filter ? `?status=${filter}` : "";
      const { data } = await api.get(`/admin/contacts/${qs}`);
      setContacts(data.contacts || []);
    } catch (err) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, [filter]);

  const updateStatus = async (contact, status) => {
    try {
      await api.put(`/admin/contacts/${contact._id}`, { status });
      toast.success(`Marked as ${status}`);
      setDetail(detail && detail._id === contact._id ? { ...detail, status } : detail);
      fetch();
    } catch (err) { toast.error("Failed to update"); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/admin/contacts/${detail._id}`, { admin_notes: detail.admin_notes || "" });
      toast.success("Notes saved");
    } catch (err) { toast.error("Save failed"); }
    finally { setSavingNotes(false); }
  };

  const remove = async (c) => {
    if (!confirm(`Delete contact from ${c.contact_name || c.email}?`)) return;
    try {
      await api.delete(`/admin/contacts/${c._id}`);
      toast.success("Deleted");
      setDetail(null);
      fetch();
    } catch (err) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-contacts">
      <div>
        <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Mail className="h-6 w-6 text-[#FF6B00]" /> Contact Submissions
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">B2B leads + general contact form submissions.</p>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilter("")} className={`px-4 py-2 rounded-lg text-xs font-semibold transition ${!filter ? "bg-[#FF6B00] text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="filter-all">
          All ({contacts.length})
        </button>
        {STATUS_OPTIONS.map((s) => (
          <button key={s.key} onClick={() => setFilter(s.key)} className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg text-xs font-semibold transition ${filter === s.key ? "bg-[#FF6B00] text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`filter-${s.key}`}>
            <s.icon className="h-3 w-3" /> {s.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {loading ? <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>
          : contacts.length === 0 ? <div className="p-10 text-center text-sm text-slate-400">No contacts found</div>
          : <div className="divide-y divide-slate-100">
              {contacts.map((c) => (
                <div key={c._id} onClick={() => setDetail(c)} className="p-4 hover:bg-slate-50/60 cursor-pointer" data-testid={`contact-row-${c._id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-['Outfit'] font-bold text-slate-900">{c.contact_name || c.name || "Unknown"}</span>
                        {c.company_name && <span className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="h-3 w-3" /> {c.company_name}</span>}
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${c.status === "new" ? "bg-blue-100 text-blue-700" : c.status === "qualified" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{c.status || "new"}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        {c.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</span>}
                        {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</span>}
                        <span>{new Date(c.created_at).toLocaleDateString()}</span>
                      </div>
                      {c.message && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.message}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="contact-detail-modal">
            <div className="sticky top-0 bg-white border-b border-slate-100 p-5 flex items-center justify-between">
              <div>
                <h3 className="font-['Outfit'] text-lg font-bold text-slate-900">{detail.contact_name || detail.name}</h3>
                <p className="text-xs text-slate-500">{detail.email}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDetail(null)}>✕</Button>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                {detail.company_name && <Info label="Company" value={detail.company_name} />}
                {detail.phone && <Info label="Phone" value={detail.phone} />}
                {detail.industry && <Info label="Industry" value={detail.industry} />}
                {detail.volume && <Info label="Monthly Volume" value={detail.volume} />}
                <Info label="Submitted" value={new Date(detail.created_at).toLocaleString()} />
                <Info label="Status" value={detail.status || "new"} />
              </div>

              {detail.message && (
                <div>
                  <Label className="text-xs">Message</Label>
                  <div className="mt-1 p-3 rounded-xl bg-slate-50 border border-slate-100 text-sm text-slate-700 whitespace-pre-wrap">{detail.message}</div>
                </div>
              )}

              <div>
                <Label className="text-xs">Admin Notes</Label>
                <textarea
                  value={detail.admin_notes || ""}
                  onChange={(e) => setDetail({ ...detail, admin_notes: e.target.value })}
                  className="w-full mt-1 rounded-xl border border-slate-200 p-3 text-sm h-20 resize-none focus:border-[#FF6B00] focus:outline-none"
                  placeholder="Private notes (visible only to admins)"
                  data-testid="admin-notes"
                />
                <Button size="sm" onClick={saveNotes} disabled={savingNotes} className="mt-2 rounded-lg bg-[#FF6B00] hover:bg-[#E65A00]" data-testid="save-notes-btn">
                  {savingNotes ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save Notes"}
                </Button>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <Label className="text-xs">Update Status</Label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {STATUS_OPTIONS.map((s) => (
                    <Button key={s.key} size="sm" variant={detail.status === s.key ? "default" : "outline"} onClick={() => updateStatus(detail, s.key)} className={`rounded-lg ${detail.status === s.key ? "bg-[#FF6B00] hover:bg-[#E65A00]" : ""}`} data-testid={`set-status-${s.key}`}>
                      <s.icon className="h-3 w-3 mr-1" /> {s.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4 flex justify-end">
                <Button variant="outline" onClick={() => remove(detail)} className="border-red-200 text-red-600 rounded-lg" data-testid="delete-contact-btn">
                  <Trash2 className="h-3 w-3 mr-1" /> Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{value ?? "—"}</p>
    </div>
  );
}
